// UserOrderHistory.js - User Order History Modal Component
import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Button, Spin, Empty, Card } from "antd";
import {
  FileTextIcon,
  CalendarIcon,
  EyeIcon,
} from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import OrderDetails from "../Orders/OrderDetails";

function UserOrderHistory({ visible, userId, onClose }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const [historyResponse, getHistoryRequest] = useApi();

  useEffect(() => {
    if (visible && userId) {
      fetchOrderHistory(1);
    }
  }, [visible, userId]);

  const fetchOrderHistory = (page) => {
    getHistoryRequest(
      null,
      API_METHODS.GET,
      `/admin/users/history/${userId}?pageNumber=${page}`
    );
    setCurrentPage(page);
  };

  const handlePageChange = (page) => {
    fetchOrderHistory(page);
  };

  const handleViewOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setShowOrderDetails(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "green";
      case "completed": return "blue";
      case "pending": return "orange";
      case "cancelled": return "red";
      case "payment_failed": return "red";
      default: return "gray";
    }
  };

  const columns = [
    {
      title: "Order Details",
      key: "order_details",
      render: (record) => (
        <div>
          <div style={{ fontWeight: "600", color: "#333" }}>
            #{record.order_number}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            <CalendarIcon style={{ width: "12px", marginRight: "4px" }} />
            Puja Date: {new Date(record.booking_date).toLocaleDateString("en-IN")}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Ordered: {new Date(record.created_on).toLocaleDateString("en-IN")}
          </div>
        </div>
      ),
    },
    {
      title: "Services",
      dataIndex: "services",
      key: "services",
      render: (services) => (
        <div style={{ maxWidth: "200px" }}>
          {services ? (
            <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
              {services.length > 60 ? `${services.substring(0, 60)}...` : services}
            </div>
          ) : (
            <span style={{ color: "#999" }}>No services</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      align: "center",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || "UNKNOWN"}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_on",
      key: "created_on",
      render: (date) => (
        <div style={{ fontSize: "12px" }}>
          {new Date(date).toLocaleDateString("en-IN")}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <Button
          size="small"
          icon={<EyeIcon style={{ width: "14px" }} />}
          onClick={() => handleViewOrder(record.id)}
          style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}
        >
          View
        </Button>
      ),
    },
  ];

  const user = historyResponse.data?.user;
  const orders = historyResponse.data?.orders || [];
  const pagination = historyResponse.data?.pagination || {};

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <FileTextIcon style={{ marginRight: "8px", color: "#f97316" }} />
            Order History
            {user && (
              <span style={{ marginLeft: "8px", color: "#666", fontWeight: "normal" }}>
                - {[user.first_name, user.last_name].filter(Boolean).join(" ")}
              </span>
            )}
          </div>
        }
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={[
          <Button key="close" onClick={onClose}>Close</Button>,
        ]}
      >
        {historyResponse.isLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <p style={{ marginTop: "16px" }}>Loading order history...</p>
          </div>
        ) : (
          <div>
            {user && (
              <Card size="small" className="mb-4">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h4 style={{ margin: 0 }}>
                      {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                    </h4>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#f97316" }}>
                      {pagination.count || 0}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Total Orders</div>
                  </div>
                </div>
              </Card>
            )}

            {orders.length > 0 ? (
              <Table
                columns={columns}
                dataSource={orders}
                loading={historyResponse.isLoading}
                rowKey="id"
                pagination={{
                  current: currentPage,
                  total: pagination.count || 0,
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                  onChange: handlePageChange,
                }}
                scroll={{ x: 600 }}
                size="small"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No orders found for this user"
              />
            )}
          </div>
        )}
      </Modal>

      <OrderDetails
        visible={showOrderDetails}
        orderId={selectedOrderId}
        onClose={() => { setShowOrderDetails(false); setSelectedOrderId(null); }}
        onStatusUpdate={() => { setShowOrderDetails(false); fetchOrderHistory(currentPage); }}
      />
    </>
  );
}

export default UserOrderHistory;
